import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Upload, CheckCircle2, AlertTriangle } from 'lucide-react';
import { getAuthHeaders } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL = '__all__';
const STATS = [
  ['create', 'New SKUs', 'text-green-700'],
  ['update', 'Updated', 'text-blue-700'],
  ['unchanged', 'Unchanged', 'text-gray-500'],
  ['error', 'Errors (skipped)', 'text-red-600'],
  ['new_mappings', 'New mappings', 'text-purple-700'],
];

const TemplateStep = ({ country }) => {
  const [brands, setBrands] = useState([]);
  const [brand, setBrand] = useState(ALL);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/excel/sku-master/brands`, { params: { country }, headers: getAuthHeaders() })
      .then((res) => setBrands(res.data.brands)).catch(() => setBrands([]));
  }, [country]);

  const download = async () => {
    setBusy(true);
    try {
      const params = { country, ...(brand !== ALL && { brand }) };
      const res = await axios.get(`${API}/excel/sku-master/template`, { params, headers: getAuthHeaders(), responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `SKU_MASTER_${brand === ALL ? 'ALL' : brand}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download template');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border p-3" data-testid="sku-master-template-step">
      <span className="text-[13px] font-medium">1. Download master file</span>
      <Select value={brand} onValueChange={setBrand}>
        <SelectTrigger className="h-8 w-40 text-[13px]" data-testid="sku-master-brand-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>All brands</SelectItem>
          {brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button size="sm" variant="outline" onClick={download} disabled={busy} data-testid="sku-master-template-download">
        <Download className="mr-1 h-3.5 w-3.5" />{busy ? 'Preparing…' : 'Download'}
      </Button>
    </div>
  );
};

const Summary = ({ summary }) => (
  <div className="grid grid-cols-5 gap-2" data-testid="sku-master-preview-summary">
    {STATS.map(([key, label, color]) => (
      <div key={key} className="rounded-lg bg-gray-50 p-2 text-center">
        <div className={`text-xl font-semibold ${color}`} data-testid={`sku-master-count-${key}`}>{summary[key]}</div>
        <div className="text-[11px] text-gray-500">{label}</div>
      </div>
    ))}
  </div>
);

const FIELD_LABELS = {
  product_name: 'Product Name', parent_model: 'Model', channel_mode: 'Channel', cost_price: 'Cost',
  selling_price: 'Selling Price', lead_time_days: 'Lead Time', image_url: 'Image', moq: 'MOQ',
};
const label = (f) => FIELD_LABELS[f] || f.charAt(0).toUpperCase() + f.slice(1);

const rowDetail = (row) => {
  if (row.errors.length) return row.errors.join(' · ');
  const parts = [];
  if (row.changes.length) parts.push(`${row.action === 'create' ? 'Fields' : 'Changes'}: ${row.changes.map(label).join(', ')}`);
  if (row.identifiers.length) parts.push(`+${row.identifiers.length} marketplace code(s)`);
  if (row.aliases.length) parts.push(`+old code ${row.aliases.join(', ')}`);
  return [...parts, ...row.warnings].join(' · ');
};

const PreviewRows = ({ rows }) => {
  const shown = rows.filter((r) => r.action !== 'unchanged').sort((a, b) => (a.action === 'error' ? -1 : 0) - (b.action === 'error' ? -1 : 0));
  if (!shown.length) return <p className="text-[13px] text-gray-500">Nothing to change — file matches the current master.</p>;
  return (
    <div className="max-h-64 overflow-auto rounded-lg border" data-testid="sku-master-preview-rows">
      <table className="w-full text-[12px]">
        <thead className="sticky top-0 bg-gray-50 text-left text-gray-500">
          <tr><th className="p-2">Row</th><th className="p-2">SKU</th><th className="p-2">Action</th><th className="p-2">Details</th></tr>
        </thead>
        <tbody>
          {shown.map((r) => (
            <tr key={`${r.source_row}-${r.sku_code}`} className="border-t" data-testid={`sku-master-row-${r.source_row}`}>
              <td className="p-2 text-gray-400">{r.source_row}</td>
              <td className="p-2 font-mono">{r.sku_code || '—'}</td>
              <td className={`p-2 font-medium ${r.action === 'error' ? 'text-red-600' : 'text-gray-700'}`}>{r.action}</td>
              <td className="p-2 text-gray-600">{rowDetail(r)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const postFile = (path, file, country) => {
  const form = new FormData();
  form.append('file', file);
  form.append('country', country);
  return axios.post(`${API}/excel/sku-master/${path}`, form, { headers: getAuthHeaders() });
};

export const SKUMasterUploadDialog = ({ open, onOpenChange, country, onImported }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setFile(null); setPreview(null); };
  const close = (value) => { if (!value) reset(); onOpenChange(value); };

  const choose = async (e) => {
    const picked = e.target.files?.[0];
    e.target.value = '';
    if (!picked) return;
    setBusy(true);
    try {
      const res = await postFile('preview', picked, country);
      setFile(picked);
      setPreview(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not read file');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const res = await postFile('confirm', file, country);
      toast.success(res.data.message);
      onImported?.();
      close(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const toApply = preview ? preview.summary.create + preview.summary.update : 0;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-3xl" data-testid="sku-master-upload-dialog">
        <DialogHeader>
          <DialogTitle>Upload SKU master file</DialogTitle>
          <DialogDescription>
            One file for SKU details, old codes and marketplace codes. Filled cells update, blank cells keep current values, SKUs not in the file are untouched.
          </DialogDescription>
        </DialogHeader>
        <TemplateStep country={country} />
        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed p-3 text-[13px] hover:bg-gray-50" data-testid="sku-master-file-label">
          <Upload className="h-4 w-4" />
          <span className="font-medium">2. {file ? `Selected: ${file.name}` : 'Choose filled file (.xlsx / .csv)'}</span>
          {busy && !preview && <span className="text-gray-500">Checking…</span>}
          <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={choose} disabled={busy} data-testid="sku-master-file-input" />
        </label>
        {preview && (
          <div className="space-y-3">
            <Summary summary={preview.summary} />
            <PreviewRows rows={preview.rows} />
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1 text-[12px] text-gray-500">
                {preview.summary.error ? <AlertTriangle className="h-3.5 w-3.5 text-amber-500" /> : <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />}
                {preview.summary.error ? 'Rows with errors will be skipped. Fix and re-upload anytime.' : 'All rows are valid.'}
              </span>
              <Button onClick={confirm} disabled={busy || (!toApply && !preview.summary.new_mappings)} data-testid="sku-master-confirm-button">
                {busy ? 'Saving…' : `3. Confirm ${toApply} SKU${toApply === 1 ? '' : 's'}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
